import Vue from "vue";
import VueRouter from "vue-router";
import { canNavigate } from "@/libs/acl/routeProtection";
import {
  isUserLoggedIn,
  getUserData,
  getHomeRouteForLoggedInUser,
} from "@/auth/utils";
import pages from "./routes/pages";
import user from "./routes/user";
import settings from "./routes/settings";
import events from "./routes/events";
import store from "@/store";
import eventsStoreModule from "@/views/events/eventsStoreModule";

Vue.use(VueRouter);

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      path: "/",
      name: "dashboard",
      component: () => import("@/views/Dashboard/Dashboard.vue"),
      meta: {
        Permission: "",
      },
    },

    {
      path: "/dashboard",
      redirect: "/",
    },

    {
      path: "/dashboard/event/:id",
      name: "dashboard-event-view",
      component: () => import("@/views/Dashboard/EventView.vue"),
      meta: {
        Permission: "no_permission_required",
      },
    },

    {
      path: "/action-plan-tool",
      name: "action-plan-tool",
      component: () => import("@/views/Tool/ActionPlanTool.vue"),
      meta: {
        Permission: "action_tool_page_menu",
      },
    },

    {
      path: "/working-group-tool",
      name: "working-group-tool",
      component: () => import("@/views/Tool/WorkingGroupTool.vue"),
      meta: {
        Permission: "working_tool_page_menu",
      },
    },

    {
      path: "/risk-management-tool",
      name: "risk-management-tool",
      component: () => import("@/views/Tool/RiskManagementTool.vue"),
      meta: {
        Permission: "action_tool_page_menu",
      },
    },

    // ===== Governance (private to Chairpersons / Directors) =====
    {
      path: "/governance/working-groups",
      name: "governance-working-groups",
      component: () => import("@/views/Tool/WorkingGroupTool.vue"),
      props: { governance: true },
      meta: {
        Permission: "view_governance_working_groups",
      },
    },
    {
      path: "/governance/events",
      name: "governance-events",
      component: () => import("@/views/Governance/GovernanceEvents.vue"),
      meta: {
        Permission: "view_governance_events",
      },
    },
    {
      path: "/governance/events/add",
      name: "governance-event-add",
      component: () => import("@/views/Governance/GovernanceEventForm.vue"),
      meta: {
        Permission: "create_governance_event",
      },
    },
    {
      path: "/governance/events/:id/edit",
      name: "governance-event-edit",
      component: () => import("@/views/Governance/GovernanceEventForm.vue"),
      meta: {
        Permission: "edit_governance_event",
      },
    },
    {
      path: "/governance/events/:id",
      name: "governance-event-view",
      component: () => import("@/views/Governance/GovernanceEventView.vue"),
      meta: {
        Permission: "view_governance_events",
      },
    },
    {
      path: "/governance/calendar",
      name: "governance-calendar",
      component: () => import("@/views/Governance/BoardCalendar.vue"),
      meta: {
        Permission: "view_governance_calendar",
      },
    },
    {
      path: "/governance/reports",
      name: "governance-reports",
      component: () => import("@/views/Governance/GovernanceReports.vue"),
      meta: {
        Permission: "view_governance_report",
      },
    },

     {
      path: "/training-calendar",
      name: "training-calendar",
      component: () => import("@/views/events/TrainingCalendar.vue"),
      meta: {
        Permission: "",
      },
    },

    {
      path: "/report",
      name: "report",
      component: () => import("@/views/Report/Report.vue"),
      meta: {
        Permission: "report_page_menu",
      },
    },

    {
      path: "signout",
      name: "signout",
      component: () => import("@/views/LogOut.vue"),
      meta: {
        Permission: "no_permission_required",
      },
    },

    ...pages,
    ...user,
    ...settings,
    ...events,

    {
      path: "*",
      redirect: "error-404",
    },
  ],
});

// Ensure events module registered for guard usage
const ensureEventsModule = () => {
  const MODULE = 'events';
  if (!store.hasModule(MODULE)) {
    store.registerModule(MODULE, eventsStoreModule);
  }
};

// Async helper to determine if navigating user is an admin for the target event
const isEventAdmin = async (to) => {
  try {
    const userData = JSON.parse(localStorage.getItem('userData')) || {};
    const userId = userData.id;
    if (!userId) return false;

    // Only consider certain event-scoped route names
    const eventScopedNames = ['event-view','event-applications','event-duplicate'];
    if (!eventScopedNames.includes(to.name)) return false;

    const eventId = to.params.id;
    if (!eventId) return false;

    ensureEventsModule();
    const res = await store.dispatch('events/getEventById', { id: eventId });
    const ev = res && res.data ? res.data : {};
    const admins = Array.isArray(ev.admins) ? ev.admins : [];
    return admins.some(a => String(a.id) === String(userId));
  } catch (e) {
    return false;
  }
};

// Async helper: event edit is only allowed for the creator or event admins
const canEditEvent = async (to) => {
  try {
    const userData = JSON.parse(localStorage.getItem('userData')) || {};
    const userId = userData && (userData.id || userData.user_id);
    if (!userId) return false;

    if (to.name !== 'event-edit') return false;
    const eventId = to.params.id;
    if (!eventId) return false;

    ensureEventsModule();
    const res = await store.dispatch('events/getEventById', { id: eventId });
    const ev = res && res.data ? res.data : {};

    const creatorId = ev.created_by || ev.createdBy;
    const isCreator = creatorId !== null && creatorId !== undefined && String(creatorId) === String(userId);

    const admins = Array.isArray(ev.admins) ? ev.admins : (Array.isArray(ev.event_admins) ? ev.event_admins : []);
    const isAdmin = admins.some(a => String(a && a.id) === String(userId));

    return isCreator || isAdmin;
  } catch (e) {
    return false;
  }
};

router.beforeEach(async (to, _, next) => {
  const isLoggedIn = isUserLoggedIn();

  if (!canNavigate(to)) {
    if (!isLoggedIn) return next({ name: "auth-login" });
  }

  // Redirect if logged in
  if (to.meta.redirectIfLoggedIn && isLoggedIn) {
    const userData = getUserData();
    next(getHomeRouteForLoggedInUser(userData ? userData.role : null));
  }

  if (isLoggedIn) {
    const userData = JSON.parse(localStorage.getItem("userData")) || {};
    const userPerms = JSON.parse(localStorage.getItem("userPerms")) || [];
    // System-wide admin roles always allowed
    if (["supermanager", "admin", "Administrator"].includes(userData.role)) {
      return next();
    }

    // No permission required routes
    if (to.meta.Permission === "no_permission_required") {
      return next();
    }

    // Special case: event edit is restricted to the event creator or event admins
    if (to.meta.Permission === 'edit_event' && to.name === 'event-edit') {
      if (await canEditEvent(to)) {
        return next();
      }
      return next({ path: "not-authorized" });
    }

    // Has base permission
    if (userPerms.includes(to.meta.Permission)) {
      return next();
    }

    // Special case: legacy allowance for action tool page
    if (to.meta.Permission === "action_tool_page_menu") {
      return next();
    }

    // Event-admin override: allow if user is admin of target event
    if (await isEventAdmin(to)) {
      return next();
    }

    // Deny
    return next({ path: "not-authorized" });
  }

  // Not logged in - allow (other logic earlier may redirect to login if needed)
  return next();
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

export default router;
